import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, updateDoc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import '../GestionUsuarios.css'; // Reutilizaremos los estilos de GestionUsuarios
import EditModal from './EditModal'; // Importar el modal reutilizable

const GestionClientes = () => {
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchClientes = useCallback(async () => {
        setLoading(true);
        try {
            const clientesCollection = collection(db, 'SicTablaClientes');
            const clientesSnapshot = await getDocs(clientesCollection);
            const clientesList = clientesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            setClientes(clientesList);
        } catch (error) {
            console.error("Error al obtener clientes: ", error);
            alert("No se pudieron cargar los clientes.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    const handleSave = async (recordData) => {
        try {
            const isCreating = !recordData.id;
            const docId = isCreating ? recordData.cedula : recordData.id;

            if (!docId) {
                alert("La cédula es obligatoria para crear un nuevo registro.");
                return;
            }

            const recordRef = doc(db, 'SicTablaClientes', docId);

            if (isCreating) {
                await setDoc(recordRef, { 
                    ...recordData, 
                    fechacreacion: serverTimestamp(),
                    responsable: auth.currentUser?.email || 'sistema'
                });
            } else {
                await updateDoc(recordRef, {
                    nombre: recordData.nombre,
                    correo: recordData.correo || '',
                    telefonoOficina: recordData.telefonoOficina || '',
                    telefonoCelular: recordData.telefonoCelular || '',
                    fechaModificacion: serverTimestamp(),
                });
            }
            await fetchClientes();
            closeModal();
        } catch (error) {
            console.error("Error al actualizar el registro: ", error);
            alert("Error al guardar los cambios.");
        }
    };

    const handleDelete = async (record) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar a "${record.nombre}"? Esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            await deleteDoc(doc(db, 'SicTablaClientes', record.id));
            await fetchClientes(); // Recarga la lista de clientes
        } catch (error) {
            console.error("Error al eliminar el cliente: ", error);
            alert("Error al eliminar el cliente.");
        }
    };

    const openModal = (record) => {
        setEditingRecord(record || null); // Pasa null si es para crear
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingRecord(null);
    };

    const handleSelect = (record) => {
        // Por ahora, solo muestra una alerta. Puedes expandir esta funcionalidad.
        alert(`Cliente seleccionado:\n\nNombre: ${record.nombre}\nCédula: ${record.cedula}\nCorreo: ${record.correo}`);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            let date;
            if (timestamp.toDate) { // Es un objeto Timestamp de Firestore
                date = timestamp.toDate();
            } else if (typeof timestamp === 'string') { // Es un string de fecha
                date = new Date(timestamp);
            } else { // Ya es un objeto Date
                date = timestamp;
            }
            return new Intl.DateTimeFormat('es-CR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
        } catch (error) {
            console.error("Error al formatear fecha:", error);
            return 'Fecha inválida';
        }
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return 'N/A';
        // Eliminar todos los caracteres no numéricos
        const digitsOnly = String(phoneNumber).replace(/\D/g, ''); // Elimina todo lo que no sea dígito
        if (digitsOnly.length === 8) {
            return `${digitsOnly.substring(0, 4)}-${digitsOnly.substring(4, 8)}`;
        }
        return phoneNumber; // Devolver el original si no tiene 8 dígitos
    };

    const filteredClientes = clientes.filter(cliente => {
        const term = searchTerm.toLowerCase();
        return (
            cliente.nombre?.toLowerCase().includes(term) ||
            cliente.cedula?.toLowerCase().includes(term) ||
            cliente.correo?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Cargando clientes...</p></div>;
    }

    return (
        <div className="gestion-usuarios-container">
            <div className="component-header">
                <h1>👨‍💼 Gestión de Clientes</h1>
                <div className="header-actions">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, cédula o correo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="header-actions">
                    {/*<div className="tooltip-container">
                        <button className="btn btn-primary icon-btn-add" onClick={() => openModal(null)}>
                            <span className="icon-add"></span>
                        </button>
                         //<span className="tooltip-text">Crear Cliente</span> */}
                        <button className="btn btn-primary" onClick={() => openModal(null)}>Crear Cliente</button>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="table-container">
                    <table className="usuarios-table">
                        <thead>
                            <tr>
                                <th>Cédula</th>
                                <th>Nombre</th>
                                <th>Correo</th>
                                <th>Teléfono</th>
                                <th>Responsable</th>
                                <th>Fecha Creación</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClientes.map(cliente => (
                                <tr key={cliente.id}>
                                    <td>{cliente.cedula}</td>
                                    <td>{cliente.nombre}</td>
                                    <td>{cliente.correo || 'N/D'}</td>
                                    <td>{formatPhoneNumber(cliente.telefonoOficina || cliente.telefono)}</td>
                                    <td>{cliente.responsable}</td>
                                    <td>{formatDate(cliente.fechacreacion)}</td>
                                    <td className="actions-cell">
                                        <div className="tooltip-container">
                                            <button className="icon-btn" onClick={() => handleSelect(cliente)}>✅</button>
                                            <span className="tooltip-text">Seleccionar</span>
                                        </div>
                                        <div className="tooltip-container">
                                            <button className="icon-btn" onClick={() => openModal(cliente)}>✏️</button>
                                            <span className="tooltip-text">Editar</span>
                                        </div>
                                        <div className="tooltip-container">
                                            <button className="icon-btn danger" onClick={() => handleDelete(cliente)}>🗑️</button>
                                            <span className="tooltip-text">Eliminar</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <EditModal
                    record={editingRecord}
                    onClose={closeModal}
                    onSave={handleSave}
                    type="Cliente"
                />
            )}
        </div>
    );
};

export default GestionClientes;